import { InstructorLegendCompact } from '../../../../../../components/generic';
import { DateCell } from '../../../../../../components/tables/cellComponents/DateCell';
import { InstructorsCell } from '../../../../../../components/tables/cellComponents/InstructorsCell';
import { LessonPlansCell } from '../../../../../../components/tables/cellComponents/LessonPlansCell';
import { SimpleTable } from '../../../../../../components/tables/SimpleTable';
import { Course } from '../../../../../../types/course.type';
import { Lesson } from '../../../../../../types/lesson.type';
import { getScheduleColumns } from '../../../../../../utils/consts/columnDefs/sessionColumns';
import { getLessonInstructorDisplay } from '../../../../../../utils/lessonInstructorUtils';
import { getLessonPlanCellProps, buildVirtualAssignments } from '../../../../../../utils/lessonPlanUtils';
import { lessonAssignmentActionFn } from './SessionDetail.types';
import { getLessonSessionTimeDisplay } from '../../../../../../utils/dateUtils';
import { LessonScheduleActions } from './LessonScheduleActions';
import { OnlineJoinEventIcon } from './OnlineJoinEventIcon';
import {useMemo} from "react";

interface Props {
  course: Course | undefined;
  sortedLessons: Lesson[];
  featuredTlps: any[];
  actionCallback: lessonAssignmentActionFn;
}

const getNextUpcomingDate = (lessons: Lesson[]) => {
  const now = new Date();
  const futureDates = lessons.map((l) => new Date(l.date)).filter((d) => d > now);
  if (futureDates.length === 0) return null;
  return new Date(Math.min(...futureDates.map((d) => d.getTime())));
};

export const SessionClassSchedule = ({
  course,
  sortedLessons,
  featuredTlps,
  actionCallback
  }: Props) => {
    const virtualAssignments = useMemo(
        () => buildVirtualAssignments(sortedLessons.map((l) => l.date), featuredTlps),
        [sortedLessons, featuredTlps]
    );
  return (
      <>
        <InstructorLegendCompact />

      {/* Desktop — top-align cells so coverage rows line up with instructor rows */}
      <div className="d-none d-lg-block session-class-schedule-table">
        <SimpleTable
          columns={getScheduleColumns(actionCallback)}
          data={sortedLessons.map((lesson) => ({
              ...lesson,
              course,
              virtual_lesson_plan: virtualAssignments.get(lesson.date.slice(0, 10)) ?? null
          }))}
        />
      </div>

      {/* Mobile */}
      <div className="d-lg-none">
        {sortedLessons.map((lesson, id) => {
          const isNext =
            new Date(lesson.date).getTime() === getNextUpcomingDate(sortedLessons)?.getTime();

          const instructors = course ? getLessonInstructorDisplay(lesson, course) : [];

          return (
            <div
              key={id}
              className="div-rounded mb-3"
              style={{
                padding: '15px',
                backgroundColor: isNext ? '#f0f4d6' : '#f8f9fa',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                minHeight: '120px',
                height: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}
            >
              <h2
                className="mb-0"
                style={{ fontSize: '1.2rem', flexShrink: 0 }}
              >
                <DateCell
                  date={lesson.date}
                  format="EEEE"
                />
                {' - '}
                <DateCell date={lesson.date} />
              </h2>

              <div
                style={{ flexShrink: 0 }}
                className="d-inline-flex align-items-center gap-2"
              >
                <span>{course ? getLessonSessionTimeDisplay(lesson, course) : null}</span>
                {course && (
                  <OnlineJoinEventIcon
                    course={course}
                    lessonDate={lesson.date}
                    lessonId={lesson.id}
                  />
                )}
              </div>

              <h3
                className="mb-0"
                style={{ fontSize: '1rem', flexShrink: 0 }}
              >
                Lesson Plan
              </h3>
              <div style={{ flex: 1, overflowY: 'auto', maxHeight: '200px' }}>
                  <LessonPlansCell
                      {...getLessonPlanCellProps(
                          lesson.lesson_plans,
                          virtualAssignments.get(lesson.date.slice(0, 10))
                      )}
                  />
              </div>

              {instructors.length > 0 && (
                <InstructorsCell
                  instructors={instructors}
                  lessonAssignments={lesson.lesson_assignments}
                />
              )}

              {course && (
                <LessonScheduleActions
                  lesson={lesson}
                  course={course}
                  actionCallback={actionCallback}
                  stacked
                />
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};
